import { createAction, createReducer } from 'redux-act';
import Immutable from 'seamless-immutable';
import moment from 'moment';
import { PURGE } from 'redux-persist';
import { createAsyncAction } from '../redux/action-util';

export const INITIAL_STATE = Immutable({
  user: {},
  onPremiseUser: null,
  onPremiseCashbox: null,
  onPremiseWaiterAuthorizations: null,
  accessToken: '',
  refreshToken: null,
  expiresIn: 0,
  expiresAt: null,
  loggedIn: false,
  isLoading: false,
  errorMessage: null,
  roles: [],
  isAdmin: false,
  refreshTokensCache: {},
});

export const loginAction = createAsyncAction('LOGIN');
export const logoutAction = createAction('LOGOUT');
export const refreshAccessTokenAction = createAsyncAction('REFRESH_ACCESSTOKEN');
export const setOnPremiseUserAction = createAction('SET_ONPREMISE_USER');
export const setOnPremiseCashboxAction = createAction('SET_ONPREMISE_CASHBOX');
export const setOnPremiseWaiterAuthorizationsAction = createAction('SET_ONPREMISE_WAITERAUTHORIZATIONS');
export const loadOnPremiseUserLoginInfoAction = createAsyncAction('onPremiseUserLoginInfo');
export const updateOnPremiseUserLoginInfoAction = createAsyncAction('updateOnPremiseUserLoginInfo');

export const changePasswordAction = createAsyncAction('changePassword');
export const resetPasswordAction = createAsyncAction('resetPassword');

export const activateAccountAction = createAsyncAction('activateAccount');
export const confirmEmailAction = createAsyncAction('confirmEmail');

export const setErrorMessageAction = createAction('setErrorMessage');
const getExpirationDate = expiresIn => moment().add(expiresIn - 60, 's').toDate();

const reducers = createReducer({
  [loginAction.INIT]: state => state.merge({
    errorMessage: null,
    isLoading: true,
  }),
  [logoutAction]: (state) => {
    let { refreshTokensCache } = state;
    if (refreshTokensCache && state.onPremiseUser) {
      refreshTokensCache = refreshTokensCache.without(state.onPremiseUser.externalId);
    }

    return INITIAL_STATE
      .set('onPremiseUser', state.onPremiseUser)
      .set('refreshTokensCache', refreshTokensCache);
  },
  [loginAction.DONE]: (state, {
    user, accessToken, refreshToken, expiresIn, roles,
  }) => {
    const { onPremiseUser = {} } = state;

    let newState = state
      .merge({
        loggedIn: true,
        user,
        accessToken,
        refreshToken,
        expiresIn,
        expiresAt: getExpirationDate(expiresIn),
        errorMessage: null,
        isLoading: false,
        roles,
        isAdmin: roles.includes('Admin'),
      });

    // store refreshtokens for usage by onPremise users
    if (onPremiseUser) {
      newState = newState.setIn(
        ['refreshTokensCache', onPremiseUser.externalId],
        { refreshToken, expiresIn, expiresAt: getExpirationDate(expiresIn) },
      );
    }

    return newState;
  },
  [loginAction.FAILED]: (state, { message }) => state.merge({
    errorMessage: message,
    accessToken: null,
    user: {},
    isLoading: false,
  }),
  [refreshAccessTokenAction.DONE]: (state, { refreshToken, accessToken, expiresIn }) => state.merge({
    loggedIn: true,
    accessToken,
    refreshToken,
    expiresIn,
    expiresAt: getExpirationDate(expiresIn),
    isLoading: false,
  }),
  [resetPasswordAction.INIT]: state => state.merge({
    isLoading: true,
    errorMessage: null,
  }),
  [resetPasswordAction.DONE]: state => state.merge({
    isLoading: false,
  }),
  [resetPasswordAction.FAILED]: (state, { message }) => state.merge({
    isLoading: false,
    errorMessage: message,
  }),
  [activateAccountAction.INIT]: state => state.merge({
    isLoading: true,
  }),
  [activateAccountAction.DONE]: state => state.merge({
    isLoading: false,
    errorMessage: null,
  }),
  [activateAccountAction.FAILED]: (state, errorMessage) => state.merge({
    isLoading: false,
    errorMessage,
  }),
  [confirmEmailAction.INIT]: state => state.merge({
    isLoading: true,
  }),
  [confirmEmailAction.Done]: state => state.merge({
    isLoading: false,
    errorMessage: null,
  }),
  [confirmEmailAction.FAILED]: (state, errorMessage) => state.merge({
    isLoading: false,
    errorMessage,
  }),
  [setErrorMessageAction]: (state, errorMessage) => state.merge({
    errorMessage,
  }),
  [setOnPremiseUserAction]: (state, { user }) => {
    // try to get refreshtokens from cache
    const { refreshToken, expiresIn, expiresAt } = state.refreshTokensCache && state.refreshTokensCache[user.externalId]
      ? state.refreshTokensCache[user.externalId]
      : {};

    return state.merge({
      onPremiseUser: user,
      refreshToken,
      expiresIn,
      expiresAt,
      loggedIn: !!refreshToken,
    });
  },
  [setOnPremiseCashboxAction]: (state, { cashbox }) => state.merge({
    onPremiseCashbox: cashbox,
  }),
  [setOnPremiseWaiterAuthorizationsAction]: (state, { authorizations }) => state.merge({
    onPremiseWaiterAuthorizations: authorizations,
  }),
  [loadOnPremiseUserLoginInfoAction.DONE]: (state, { userId, username, activated }) => state.merge({
    onPremiseUser: {
      ...state.onPremiseUser, userId, username, activated, loadFailed: false,
    },
  }),
  [loadOnPremiseUserLoginInfoAction.FAILED]: state => state.merge({
    onPremiseUser: { ...state.onPremiseUser, loadFailed: true },
  }),
  [updateOnPremiseUserLoginInfoAction.DONE]: state => state.setIn(['onPremiseUser', 'activated'], true),
  [PURGE]: () => INITIAL_STATE,
}, INITIAL_STATE);

export default reducers;
